import { useMutation, useQuery } from "@tanstack/react-query";
import { api } from "../lib/api";
import { useEffectiveUser } from "../lib/auth";
import { isNative } from "../lib/runtime";
import { Button } from "./ui/Button";

// Persistent banner that surfaces when the agency's subscription
// is past_due, canceled, or otherwise not in good standing. The
// owner gets a "Manage subscription" button (opens Stripe Customer
// Portal). Non-owners get a plain "contact your supervisor" copy.
//
// On the iOS Capacitor build the button stays for owners (Stripe
// Customer Portal counts as the existing-customer flow Apple
// allows in 3.1.3(b)) — but the marketing-side Subscribe link is
// suppressed elsewhere.
export function SubscriptionBanner() {
  const me = useEffectiveUser();
  const subQ = useQuery({
    queryKey: ["my-subscription"],
    queryFn: api.getMySubscription,
    // Every signed-in user can see this; the RLS policy lets them
    // read their own agency's row.
    enabled: !!me,
  });
  const portalMut = useMutation({
    mutationFn: () => api.startBillingPortal(),
    onSuccess: ({ url }) => { window.location.href = url; },
  });

  const sub = subQ.data;
  if (!sub) return null;

  const isOwner = me?.level === "owner";
  const status = sub.status;
  const isProblem = status === "past_due"
    || status === "canceled"
    || status === "unpaid"
    || status === "incomplete_expired"
    || status === "paused";
  if (!isProblem) return null;

  const tone = status === "past_due"
    ? "bg-amber-50 border-amber-200 text-amber-900"
    : "bg-red-50 border-red-200 text-red-900";

  const headline =
    status === "past_due" ? "Your last payment didn't go through." :
    status === "canceled" ? "Your subscription was canceled." :
    status === "unpaid"   ? "Your subscription is unpaid." :
                            "Your subscription needs attention.";

  return (
    <div className={`border-b ${tone}`}>
      <div className="px-4 md:px-6 py-2 flex flex-col md:flex-row md:items-center md:justify-between gap-2 text-sm">
        <div>
          <strong className="font-semibold">{headline}</strong>{" "}
          {isOwner
            ? "Update your billing to keep your agency active."
            : "Contact the agency owner to update billing."}
        </div>
        {isOwner && (
          <Button
            size="sm"
            onClick={() => portalMut.mutate()}
            disabled={portalMut.isPending || !sub.stripeCustomerId || isNative}
            title={isNative
              ? "Open ShiftManager on the web to manage billing."
              : undefined}
          >
            {portalMut.isPending ? "Opening…" : "Manage subscription"}
          </Button>
        )}
      </div>
    </div>
  );
}
